import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { addTask } from '@/data/tasks'
import { usePaths } from '@/hooks/usePaths'
import { usePeople, type People } from '@/hooks/usePeople'
import { useWide } from '@/hooks/useWide'
import { goToTask, revealAdded, waitForTask } from '@/lib/reveal'
import { fire } from '@/lib/writeError'
import { startOfWeek, today } from '@/logic/dates'
import { newTaskDefaults, newTaskPeople, nextWho, shownWho, whoFromDefaults, type NewTaskWho } from '@/logic/newTask'
import { itemsOnDay, orderAtEnd, orderAtTop, trayGroups } from '@/logic/plan'
import { activeCategories, useCategories } from '@/store/categories'
import { useHousehold } from '@/store/household'
import { usePrefs } from '@/store/prefs'
import { useTasks } from '@/store/tasks'
import { closeQuickAdd, openQuickAdd, useUi } from '@/store/ui'
import { Avatar, PairAvatars } from './Avatar'
import { CloseIcon, LockIcon, PencilIcon, PlusIcon } from './icons'
import './QuickAdd.css'

/** The toggle's face: my avatar, both of ours for either of us, or the other person's. */
function WhoFace({ who, people }: { who: NewTaskWho; people: People }) {
  if (who === 'partner' && people.other) return <Avatar uid={people.other.uid} people={people} />
  if (who === 'shared') return <PairAvatars people={people} />
  return <Avatar uid={people.meUid} people={people} />
}

/** Words for who a new task is for, as the toggle's label and the placeholder say it. */
function whoWords(who: NewTaskWho, otherName: string | null): string {
  if (who === 'partner' && otherName) return otherName
  return who === 'shared' ? 'either of us' : 'me'
}

/**
 * Quick add: a title, a category, who it's for and the lock for private. Enter adds the
 * task at once, with no edit panel; the field stays open for the next one, and
 * the new task is shown where it went. Who it's for starts from Settings and a tap
 * steps it through Mine, Shared and the other person's. On a phone it sits behind the round
 * button; on a wide screen the box lives in the header.
 */
export function QuickAdd({ placement }: { placement: 'floating' | 'header' }) {
  const paths = usePaths()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const wide = useWide()
  // a past week on the Plan tab is a read-only record: nothing to add there
  const pastWeek = useUi((s) => s.planWeek < startOfWeek(today()))
  const open = useUi((s) => s.quickAddOpen)
  const selecting = useUi((s) => s.selecting)
  const context = useUi((s) => s.addContext)
  const defaultContext = useUi((s) => s.defaultAddContext)
  const settings = useHousehold((s) => s.userDoc?.settings)
  const defaults = newTaskDefaults(settings, paths.uid)
  const defaultVisibility = defaults.scope
  const defaultWho = whoFromDefaults(defaults)
  const people = usePeople()
  const filterCategory = usePrefs((s) => s.filter.categoryId)
  // derived outside the selector: a new array from a selector re-renders without end
  const allCategories = useCategories((s) => s.all)
  const categories = useMemo(() => activeCategories(allCategories), [allCategories])
  const [draft, setDraft] = useState('')
  const [isPrivate, setIsPrivate] = useState(defaultVisibility === 'private')
  const [categoryId, setCategoryId] = useState<string | null>(filterCategory)
  const [who, setWho] = useState<NewTaskWho>(defaultWho)
  const inputRef = useRef<HTMLInputElement>(null)

  // the lock and who it's for start from the person's defaults; the category from the filter that is on
  const [seen, setSeen] = useState({ defaultVisibility, defaultWho, filterCategory })
  if (seen.defaultVisibility !== defaultVisibility || seen.defaultWho !== defaultWho || seen.filterCategory !== filterCategory) {
    setSeen({ defaultVisibility, defaultWho, filterCategory })
    setIsPrivate(defaultVisibility === 'private')
    setWho(defaultWho)
    setCategoryId(filterCategory)
  }

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open, context])

  if (placement === 'header' && !wide) return null
  if (placement === 'floating' && wide) return null
  // the Select bar takes quick add's place on a phone
  if (placement === 'floating' && selecting) return null

  // the header box always adds with the view's own starting point; the floating bar with where it was opened
  const ctx = placement === 'header' && !open ? defaultContext : context
  const shownCategories = categories.filter((c) => isPrivate || c.scope === 'shared')
  const category = shownCategories.find((c) => c.id === categoryId) ?? null
  const otherName = people.other?.name ?? null
  const shown = shownWho(who, isPrivate)
  // a private task is only ever mine, so stepping to anyone else makes it shared
  const stepWho = () => {
    setWho(nextWho(shown, people.other !== null))
    setIsPrivate(false)
  }

  /** Writes the task and returns its id, or null with no title. */
  const create = (): string | null => {
    const title = draft.trim()
    if (!title) return null
    setDraft('')
    const all = useTasks.getState().all
    const now = today()
    const kind = ctx.kind ?? 'once'
    const plannedDate = ctx.plannedDate ?? null
    let order: number
    if (plannedDate) order = orderAtEnd(itemsOnDay(all, plannedDate, now))
    else if (kind === 'routine') order = orderAtEnd(trayGroups(all, startOfWeek(now), now).routines)
    else order = orderAtTop(trayGroups(all, startOfWeek(now), now).nodate)
    const { id, written } = addTask(paths, {
      title,
      ...newTaskPeople(who, isPrivate, paths.uid, people.other?.uid ?? null),
      kind,
      plannedDate,
      weekOf: kind === 'routine' ? startOfWeek(plannedDate ?? now) : null,
      categoryId: category?.id ?? null,
      order,
    })
    fire(written)
    return id
  }

  const submit = () => {
    const id = create()
    if (!id) return
    void revealAdded(id, navigate)
    inputRef.current?.focus()
  }

  // adds it, brings it into view where it landed, then opens its edit panel there
  const submitAndEdit = async () => {
    const id = create()
    if (!id) return
    closeQuickAdd()
    inputRef.current?.blur()
    await waitForTask(id)
    await goToTask(id, navigate)
    navigate(`/task/${id}`)
  }

  const forWhom = isPrivate ? ', private' : who === 'shared' ? '' : `, for ${whoWords(who, otherName)}`
  const hint = `${ctx.hint ?? 'Add a task'}${category ? ` · ${category.name}` : ''}${forWhom}`

  const fields = (
    <>
      <label htmlFor={`quick-add-${placement}`} className="sr-only">
        Add a task
      </label>
      <input
        id={`quick-add-${placement}`}
        ref={inputRef}
        type="text"
        className="quick__input"
        value={draft}
        placeholder={hint}
        autoComplete="off"
        enterKeyHint="done"
        maxLength={200}
        onFocus={() => {
          if (placement === 'header' && !open) openQuickAdd()
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') {
            setDraft('')
            closeQuickAdd()
            inputRef.current?.blur()
          }
        }}
      />
      <span className="quick__cat">
        <label htmlFor={`quick-cat-${placement}`} className="sr-only">
          Category
        </label>
        {category && <span className="chip__dot quick__dot" style={{ background: `var(--${category.color})` }} aria-hidden="true" />}
        <select id={`quick-cat-${placement}`} className={`quick__select ${category ? 'quick__select--on' : ''}`} value={category?.id ?? ''} onChange={(e) => setCategoryId(e.target.value || null)}>
          <option value="">No category</option>
          {shownCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </span>
      <button
        type="button"
        className="quick__who"
        aria-label={`For ${whoWords(shown, otherName)}. Change who it's for`}
        title={`Who it's for: Mine, Shared${otherName ? ` or ${otherName}'s` : ''}`}
        onClick={stepWho}
      >
        <span className="quick__face" aria-hidden="true">
          <WhoFace who={shown} people={people} />
        </span>
      </button>
      <button
        type="button"
        className={`quick__lock ${isPrivate ? 'quick__lock--on' : ''}`}
        aria-pressed={isPrivate}
        aria-label={isPrivate ? 'Adding as private. Switch to shared' : 'Adding as shared. Switch to private'}
        onClick={() => setIsPrivate((v) => !v)}
      >
        <LockIcon size={18} />
      </button>
    </>
  )

  if (placement === 'header') {
    return (
      <div className="quick quick--header">
        {fields}
        {draft.trim() && (
          <button type="button" className="quick__edit" aria-label="Add and edit" title="Add and open the full edit panel" onClick={() => void submitAndEdit()}>
            <PencilIcon size={16} />
            Edit
          </button>
        )}
        <button type="button" className="quick__add" aria-label="Add task" onClick={submit}>
          <PlusIcon size={20} weight={2.4} />
        </button>
      </div>
    )
  }

  return (
    <>
      {open && (
        <div className="quick">
          {fields}
          {draft.trim() && (
            <button type="button" className="quick__edit" aria-label="Add and edit" title="Add and open the full edit panel" onClick={() => void submitAndEdit()}>
              <PencilIcon size={16} />
              Edit
            </button>
          )}
          <button type="button" className="quick__add" aria-label="Add task" onClick={submit}>
            <PlusIcon size={20} weight={2.4} />
          </button>
          <button type="button" className="quick__close" aria-label="Close quick add" onClick={closeQuickAdd}>
            <CloseIcon size={20} weight={2.2} />
          </button>
        </div>
      )}
      {!open && !(pastWeek && pathname.startsWith('/plan')) && (
        <button type="button" className="fab" aria-label="Add a task" onClick={() => openQuickAdd()}>
          <PlusIcon size={24} weight={2.4} />
        </button>
      )}
    </>
  )
}
